import { Outlet, useParams } from 'react-router-dom'

import { AvailableModules } from 'generated/featureDefinitions'
import { Features } from 'generated/featureDefinitions'
import { ProjectManagementContributorViewPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useGetProjectsAssignedToWorker } from 'modules/project_management/hooks/projectWorker/useGetProjectsAssignedToWorker'
import ProjectsStore from 'modules/project_management/store/models/Projects'

const ContributorViewPage = () => {
  useEffect(() => {
    track(trackingEvents.projectManagement.contributors.visitContributorViewSection)
  }, [])

  const { company } = useCurrentSession()
  const { employeeId } = useParams()
  const employeeIdNumber = Number(employeeId)
  const { navigation } = useNavigation()

  const { data, loading, error } = useQuery(ProjectManagementContributorViewPageDocument, {
    skip: !employeeIdNumber,
    variables: {
      companyId: company.id,
      employeeId: employeeIdNumber ?? 0,
      moduleId: AvailableModules.timeoff,
      featureIds: [Features.projects_contributors, Features.projects_costs],
    },
  })
  const { projectsAssigned, loading: loadingProjects } = useGetProjectsAssignedToWorker({
    employeeIds: [employeeIdNumber],
    onlyActiveProjects: false,
    currentlyAssigned: false,
    includeSubprojects: true,
  })
  const projectsStore: ProjectsStore = new ProjectsStore({ projectsAssigned })
  const accessId = data?.employees.employee?.access?.id
  const employeeName = data?.employees.employee?.fullName || ''
  const employeeLocationId = data?.employees.employee?.locationId
  const hasTimeOffModule = !!data?.companies.moduleAvailability?.available
  const hasProjectCosts = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (node) => node.id === Features.projects_costs
  )
  const hasContributorsFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_contributors
  )?.available

  const canSeeContributors = !!data?.projectManagement._policies.pagesSeeContributorsList.can

  const canRegisterFlexibleTracking =
    data?.employees.employee?._policies?.registerFlexibleTracking.can ?? false
  const canReadMyProjects =
    data?.projectManagement._policies.globalTimeRecordRead.can ||
    data?.projectManagement._policies.flexibleTimeRecordRead.can

  const trackingViewRoute = navigation.projects.contributors.contributorId.yearMonthDay.fullPath

  return (
    <ResponsivePage.Body
      showHeader={false}
      variant="wide"
      loading={loading || loadingProjects}
      error={error}
      authorized={hasContributorsFeature && canSeeContributors}
    >
      <Outlet
        context={{
          accessId,
          canReadMyProjects,
          canRegisterFlexibleTracking,
          employeeIdNumber,
          employeeLocationId,
          employeeName,
          hasTimeOffModule,
          hasProjectCosts,
          projectsStore,
          trackingViewRoute,
        }}
      />
    </ResponsivePage.Body>
  )
}

export default ContributorViewPage
